import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  IconButton,
  OutlinedInput,
  SelectChangeEvent
} from '@mui/material';
import { Close as CloseIcon, Clear as ClearIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  TaskFilters,
  TaskStatus,
  TaskType,
  TaskPriority
} from '@/types/taskTypes';

interface TaskFiltersDialogProps {
  open: boolean;
  onClose: () => void;
  filters: TaskFilters;
  onFiltersChange: (filters: TaskFilters) => void;
}

// Mock data for assignees - replace with real data from user service
const mockAssignees = [
  { id: 'user1', name: 'Sarah Johnson' },
  { id: 'user2', name: 'Mike Chen' },
  { id: 'user3', name: 'Alex Rodriguez' },
  { id: 'user4', name: 'Lisa Park' }
];

const commonTags = [
  'urgent',
  'follow-up',
  'review',
  'approval',
  'documentation',
  'compliance',
  'legal',
  'medical',
  'intake',
  'template'
];

export const TaskFiltersDialog: React.FC<TaskFiltersDialogProps> = ({
  open,
  onClose,
  filters,
  onFiltersChange
}) => {
  const [localFilters, setLocalFilters] = useState<TaskFilters>(filters);

  const handleFilterChange = (field: keyof TaskFilters, value: any) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleMultiSelectChange = (
    field: keyof TaskFilters,
    event: SelectChangeEvent<string[]>
  ) => {
    const value = event.target.value as string[];
    handleFilterChange(field, value.length > 0 ? value : undefined);
  };

  const handleDateRangeChange = (field: 'from' | 'to', date: Date | null) => {
    const currentRange = localFilters.dueDate || {};
    handleFilterChange('dueDate', {
      ...currentRange,
      [field]: date?.toISOString()
    });
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleClearFilters = () => {
    const clearedFilters: TaskFilters = {};
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const handleClose = () => {
    // Reset to current filters if user cancels
    setLocalFilters(filters);
    onClose();
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.status?.length) count++;
    if (localFilters.type?.length) count++;
    if (localFilters.priority?.length) count++;
    if (localFilters.assignedTo?.length) count++;
    if (localFilters.tags?.length) count++;
    if (localFilters.dueDate?.from || localFilters.dueDate?.to) count++;
    return count;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Filter Tasks
            </Typography>
            {getActiveFilterCount() > 0 && (
              <Chip
                label={`${getActiveFilterCount()} active`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <IconButton onClick={handleClose} sx={{ borderRadius: '6px' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Status Filter */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={localFilters.status || []}
                onChange={(e) => handleMultiSelectChange('status', e)}
                input={<OutlinedInput label="Status" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value.replace('_', ' ')}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value={TaskStatus.Pending}>Pending</MenuItem>
                <MenuItem value={TaskStatus.InProgress}>In Progress</MenuItem>
                <MenuItem value={TaskStatus.Completed}>Completed</MenuItem>
                <MenuItem value={TaskStatus.Cancelled}>Cancelled</MenuItem>
                <MenuItem value={TaskStatus.Overdue}>Overdue</MenuItem>
              </Select>
            </FormControl>

            {/* Type Filter */}
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                multiple
                value={localFilters.type || []}
                onChange={(e) => handleMultiSelectChange('type', e)}
                input={<OutlinedInput label="Type" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value.replace('_', ' ')}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value={TaskType.Approval}>Approval</MenuItem>
                <MenuItem value={TaskType.Review}>Review</MenuItem>
                <MenuItem value={TaskType.FollowUp}>Follow Up</MenuItem>
                <MenuItem value={TaskType.Information}>Information</MenuItem>
                <MenuItem value={TaskType.Custom}>Custom</MenuItem>
              </Select>
            </FormControl>

            {/* Priority Filter */}
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                multiple
                value={localFilters.priority || []}
                onChange={(e) => handleMultiSelectChange('priority', e)}
                input={<OutlinedInput label="Priority" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value={TaskPriority.Critical}>Critical</MenuItem>
                <MenuItem value={TaskPriority.High}>High</MenuItem>
                <MenuItem value={TaskPriority.Medium}>Medium</MenuItem>
                <MenuItem value={TaskPriority.Low}>Low</MenuItem>
              </Select>
            </FormControl>

            {/* Assignee Filter */}
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                multiple
                value={localFilters.assignedTo || []}
                onChange={(e) => handleMultiSelectChange('assignedTo', e)}
                input={<OutlinedInput label="Assigned To" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const assignee = mockAssignees.find(
                        (a) => a.id === value
                      );
                      return (
                        <Chip
                          key={value}
                          label={assignee?.name || value}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {mockAssignees.map((assignee) => (
                  <MenuItem key={assignee.id} value={assignee.id}>
                    {assignee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Tags Filter */}
            <FormControl fullWidth>
              <InputLabel>Tags</InputLabel>
              <Select
                multiple
                value={localFilters.tags || []}
                onChange={(e) => handleMultiSelectChange('tags', e)}
                input={<OutlinedInput label="Tags" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {commonTags.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    {tag}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Due Date Range */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Due Date Range
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="From"
                  value={
                    localFilters.dueDate?.from
                      ? new Date(localFilters.dueDate.from)
                      : null
                  }
                  onChange={(date: Date | null) =>
                    handleDateRangeChange('from', date)
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
                <DatePicker
                  label="To"
                  value={
                    localFilters.dueDate?.to
                      ? new Date(localFilters.dueDate.to)
                      : null
                  }
                  onChange={(date: Date | null) =>
                    handleDateRangeChange('to', date)
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}
        >
          <Button
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            disabled={getActiveFilterCount() === 0}
          >
            Clear All
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{ borderRadius: 2 }}
            >
              Apply Filters
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};
